import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import ApiServices from 'src/shared/Services/apiService';

interface AuthState {
  isAuthenticated: boolean;
  user: { username: string } | null;
  hasPaidBefore: boolean;
  profile: any | null;
  itemType: string | null;
  pk: string | null;
  paymentStatus: string | null;
  /** Server-owned onboarding gate — see `fetchPatientProfile`. */
  onboardingCompleted: boolean;
}

interface PatientProfileResponse {
  profile: any;
  hasPaidBefore: boolean;
  itemType: string | null;
  pk: string;
  paymentStatus: string | null;
  onboardingCompleted: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  hasPaidBefore: false,
  profile: null,
  itemType: null,
  pk: null,
  paymentStatus: null,
  onboardingCompleted: false
};

// Thunk to fetch patient profile and update state, now also checking for ItemType
export const fetchPatientProfile = createAsyncThunk<
  PatientProfileResponse,
  void
>('auth/fetchPatientProfile', async (_, { rejectWithValue }) => {
  try {
    const response = await ApiServices.getPatientProfile('');
    const profile = response?.data?.item || null;
    const hasPaidBefore = profile?.last_payment_id ? true : false;
    const itemType = profile?.ItemType || null;
    const pk = profile?.PK;
    const paymentStatus = profile?.payment_status || null;
    // Onboarding completion now travels on the profile the server already
    // returns, rather than living only in localStorage where anyone could set
    // it by hand and skip the consent step. Absent on legacy accounts, so the
    // value is coerced rather than assumed present.
    const onboardingCompleted = profile?.onboarding_completed === true;
    return { profile, hasPaidBefore, itemType, pk, paymentStatus, onboardingCompleted };
  } catch (error) {
    return rejectWithValue(
      error.response?.data?.message || 'Error fetching profile'
    );
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login(
      state,
      action: PayloadAction<{
        username: string;
        hasPaidBefore: boolean;
        profile: any;
        itemType: string | null;
        pk: string;
        paymentStatus: string | null;
        onboardingCompleted?: boolean;
      }>
    ) {
      state.isAuthenticated = true;
      state.user = { username: action.payload.username };
      state.hasPaidBefore = action.payload.hasPaidBefore;
      state.profile = action.payload.profile;
      state.itemType = action.payload.itemType;
      state.pk = action.payload.pk;
      state.paymentStatus = action.payload.paymentStatus;
      state.onboardingCompleted = action.payload.onboardingCompleted ?? false;
    },
    logout(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.hasPaidBefore = false;
      state.profile = null;
      state.itemType = null;
      state.pk = null;
      state.paymentStatus = null;
      state.onboardingCompleted = false;
    },
    setOnboardingCompleted(state, action: PayloadAction<boolean>) {
      state.onboardingCompleted = action.payload;
    },
    setHasPaidBefore(state, action: PayloadAction<boolean>) {
      state.hasPaidBefore = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPatientProfile.fulfilled, (state, action) => {
      state.profile = action.payload.profile;
      state.hasPaidBefore = action.payload.hasPaidBefore;
      state.itemType = action.payload.itemType;
      state.pk = action.payload.pk;
      state.paymentStatus = action.payload.paymentStatus;
      state.onboardingCompleted = action.payload.onboardingCompleted;
    });
  }
});

export const { login, logout, setHasPaidBefore, setOnboardingCompleted } = authSlice.actions;
export default authSlice.reducer;
